<template>
    <modal
        ref="giftVoucherPaymentModal"
        id="gift-voucher-payment-modal"
        :centered="true"
        :title="$t('giftVoucher.paymentModalTitle')"
        :show="modal.show"
        :close="modal.close"
        :closeable="modal.closeable && !paymentComplete"
        @modal-close="onHideModal"
        :tighter-close-button="true"
        size="modal-lg"
    >
        <loading-animation
            :visible="globalLoading"
            :always-show="false"
            :full-page-loader="true"
        ></loading-animation>
        <div class="modal-body pt-0">
            <div class="alert alert-success" role="alert" v-if="paymentComplete" v-t="fullyPaid ? 'giftVoucher.paymentFullPaid' : 'giftVoucher.paymentPartialPaid'"></div>
            <p class="mb-0" v-html="$t('giftVoucher.paymentModalText')"></p>
            <br />
            <GiftVoucherInput @code="codeChanged" />
            <br />
            <loading-animation
                :visible="infoLoading"
                preloader-container-id=""
                status-container-id=""
                :horizontal-centered="true"
            ></loading-animation>
            <div class="gift-voucher-info" v-if="!infoLoading">
                <div class="alert alert-danger flex-grow-1" v-if="error" v-html="error"></div>
                <template v-else-if="giftVoucher">
                    <div class="gift-voucher-info-row">
                        <span class="h4" v-t="'giftVoucher.amount'"></span>
                        <span class="h4 font-weight-normal">{{ giftVoucher.amount.toFixed(2) }} €</span>
                    </div>
                    <div class="gift-voucher-info-row">
                        <span class="h4" v-t="'giftVoucher.amountLeft'"></span>
                        <span class="h4 font-weight-normal">{{ Math.max(0, giftVoucher.amount - orderPrice).toFixed(2) }} €</span>
                    </div>
                </template>
            </div>
        </div>
        <div class="modal-footer justify-content-center">
            <button
                type="button"
                class="btn btn-primary"
                @click.prevent="pay"
                v-html="$t('shop.pay')"
                :disabled="!giftVoucher || globalLoading || infoLoading || paymentComplete"
            ></button>
            <button
                type="button"
                class="btn btn-secondary"
                @click.prevent="onHideModal"
                v-html="$t('shop.cancel')"
            ></button>
        </div>
    </modal>
</template>

<script>
import GiftVoucherInput from './GiftVoucherInput.vue';

export default {
    components: {GiftVoucherInput},
    props: {
        orderHash: {
            type: String,
            required: true,
        },
        orderPrice: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            giftVoucher: null,
            modal: {
                show: false,
                close: false,
                closable: true,
            },
            globalLoading: false,
            infoLoading: false,
            error: null,
            fullyPaid: false,
            paymentComplete: false,
        };
    },

    methods: {
        /**
         * @returns {void}
         */
        onShowModal() {
            this.modal.close = false;
            this.modal.show = true;

            this.error = null;
            this.giftVoucher = null;

            this.infoLoading = false;
            this.globalLoading = false;

            this.fullyPaid = false;
            this.paymentComplete = false;
        },

        /**
         * @returns {void}
         */
        onHideModal() {
            if (this.paymentComplete) {
                window.location.reload();
                return;
            }

            this.ignore = false;
            this.modal.show = false;
            this.modal.close = true;
        },

        codeChanged(code) {
            this.infoLoading = true;
            this.error = null;

            axios
                .post(this.validateUrl, {code})
                .then((response) => {
                    if (response.status === 200) {
                        const giftVoucher = response.data;
                        if (giftVoucher.active && giftVoucher.amount > 0) {
                            this.giftVoucher = giftVoucher;
                        } else if (giftVoucher.amount === 0) {
                            this.error = this.$t('gift_voucher.alreadyUsedCompletely');
                            this.giftVoucher = null;
                        } else {
                            this.error = this.$t('gift_voucher.inactive');
                            this.giftVoucher = null;
                        }
                    }
                })
                .catch((err) => {
                    if (err.response) {
                        switch (err.response.status) {
                            case 500:
                            case 404:
                                this.error = this.$t('giftVoucher.notFound');
                                break;
                            default:
                                this.error = err.response.data.message;
                                break;
                        }
                    }
                    this.giftVoucher = null;
                })
                .finally(() => {
                    this.infoLoading = false;
                });
        },

        pay() {
            this.globalLoading = true;

            axios
                .post(this.payUrl, { code: this.giftVoucher.code })
                .then((response) => {
                    if (response.status === 200) {
                        this.paymentComplete = true;
                        this.fullyPaid = response.data.fully_paid;
                        setTimeout(() => {
                            window.location.reload();
                        }, 1000 * 10);
                    }
                })
                .catch((err) => {
                    if (err.response) {
                        this.error = err.response.data.message;
                    }
                })
                .finally(() => {
                    this.globalLoading = false;
                });
        },
    },

    computed: {
        validateUrl() {
            return '/' + this.$language + '/payment/gift-voucher/validate';
        },
        payUrl() {
            return '/' + this.$language + '/payment/gift-voucher/pay/' + this.orderHash;
        },
    },
};
</script>

<style lang="scss" scoped>
.gift-voucher-info {
    display: flex;
    flex-direction: column;

    max-width: 66%;
    margin: 0 auto;

    @media (max-width: 992px) {
        max-width: 100%;
    }

    span {
        color: black;
    }

    .gift-voucher-info-row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }
}
</style>

<style>
.loader-container > #preloader {
    background-color: rgba(255, 255, 255, 0.5) !important;
    background-image: unset;
}
</style>
